import {
  cloneElement, isValidElement, useCallback, useEffect, useMemo, useRef, useState,
  type CSSProperties, type ReactNode,
} from 'react';
import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';
import './quotes.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AuditTrailButton } from '../../../components/AuditTrailButton';
import { useConfirm, useSaveFlow } from '../../../components/confirm';
import { formatCombo, useHotkeys } from '../../../components/hotkeys';
import { useNotify } from '../../../components/notifications';
import { focusFirstFormControl } from '../../../hooks/useAutoFocusFirstControl';
import { guardedExit, useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { fmtLocalDate } from '../../../utils/datetime';
import PreLoginDocsPanel, { type DocsOwner } from '../../settings/preLoginSettings/PreLoginDocsPanel';
import {
  calculateQuote, createQuote, downloadQuoteReport, fetchContacts, fetchProjects,
  fetchQuote, fetchQuoteLookups, reportErrorMessage, updateQuote,
} from './detailApi';
import {
  CHARGE_TYPE, blankQuote,
  type AnalysisRowInput, type ContactOption, type ItemChargeCandidate, type ItemRowInput,
  type PartyOption, type PriceCodeCandidate, type ProjectOption, type QuoteAnalysisRow,
  type QuoteDetail, type QuoteInput, type QuoteItemRow, type QuoteLookups,
} from './detailTypes';
import { ItemChargePickerDialog } from './ItemChargePickerDialog';
import { PriceCodePickerDialog, candidateTestKey } from './PriceCodePickerDialog';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Quotes — the Open Quote DETAIL view.
// Port of legacy ALPACA `CRMQuotes_DetailView` (BTLIMS.Web/Model.xafml :11543),
// caption "Open Quote". The list is REPLACED by this page, never a popup
// (standing rule 12).
//
// LAYOUT — the legacy groups, in the legacy order, two columns each:
//   Summary · Totals · Contacts · Bill To Address · [Same Address] ·
//   Report to Address · Account Management
// then the tabs Analysis Pricing · Item Charge.
//
// NO PRICE IS COMPUTED HERE. Every number in the two grids and in Totals comes
// back from `POST /quotes/calculate`, which runs the same code as the save
// (CLAUDE.md §1 — the UI is replaceable, so a quoted price must not depend on
// this component having run). This page only collects what a person types:
// quantities, discounts, descriptions and remarks.
//
// ACTION BAR, top and bottom, identical, in the reference form's order and
// wording: Save · Save and Close · Save and New · Preview · | · Cancel. Legacy
// registers all three save actions on this view (QuoteViewController
// :2676-2678), so the set is parity as well as rule 12. No Delete here — the
// standard keeps Delete out of an edit view; a quote is deleted from the list.
//
// LAYOUT (2026-10-13, owner: "improve the design with our rules"). Rebuilt on
// the SHARED detail-view classes from SettingsDetailPage.css — `.sd-wrap` /
// `.sd-head` / `.sd-actionbar` / `.sd-body` / `.sd-section-h` / `.sd-grid--side`
// / `.sd-assoc-tabbar` — after comparing it side by side with the reference
// form, Settings ▸ Inventory Management ▸ Items (standing rule 12). The page
// had its own cards, buttons and tabs before; now only the BODY scrolls and
// both action bars stay in view.
//
// ⚠️ RULE 12 DEVIATION, deliberate and flagged: bespoke rather than the shared
// <SettingsDetailPage>, because that component has no slot for two editable
// child grids with their own pickers, nor for totals that are recomputed by
// the server as the grids change. Same precedent as ReagentPrepDetailPage and
// ADR-0015's SdmsSequenceSetupPage. It still composes the shared AppDataGrid /
// AppDialog / useSaveFlow / useConfirm / useNotify / useHotkeys, and the shared
// detail-view stylesheet supplies its whole layout.
//
// NOT IN THIS WAVE: the Submit / Validate / History actions, changing a row's TAT (rush pricing) and per-parameter
// pricing. See docs/03-Code-Change-Documentation/Migration-Log.
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Open Quotes';
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

type Tab = 'analysis' | 'items' | 'attachment' | 'note';

interface Props {
  /** null = a new quote. */
  id: string | null;
  onClose: () => void;
  /** Save & New — start another blank quote. */
  onNew: () => void;
  /** Save on a new quote — carry on editing it under its real id. */
  onCreated: (id: string) => void;
  /** Something was saved or deleted; the list behind should reload. */
  onChanged: () => void;
  /** Prev / next record arrows (BO UI Standard §4) — the current page's ids. */
  siblingIds?: string[];
  onNavigate?: (id: string) => void;
}

/** The shared Attachment | Notes panel, pointed at a quote. */
const QUOTE_DOCS: DocsOwner = {
  apiBase: '/api/v1/quotes',
  formName: 'Open Quotes',
  businessObject: 'Open Quote',
  exportPrefix: 'Quote',
  noteTabLabel: 'Notes',
  countFromRows: true,
};

export default function QuoteDetailPage({
  id, onClose, onNew, onCreated, onChanged, siblingIds = [], onNavigate,
}: Props) {
  const notify      = useNotify();
  const { prompt }  = useConfirm();
  const runSave     = useSaveFlow();
  const isNew       = id === null;
  // Focus the first field once the form is showing (standing rule 14).
  const pageRef = useRef<HTMLDivElement>(null);

  const [quote, setQuote] = useState<QuoteDetail>(blankQuote);
  const [rows, setRows]   = useState<QuoteAnalysisRow[]>([]);
  const [items, setItems] = useState<QuoteItemRow[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [dirty, setDirty]     = useState(false);
  const [tab, setTab]         = useState<Tab>('analysis');
  // Reported by the documents panel once it has loaded, for the tab captions.
  const [docCounts, setDocCounts] = useState({ attachments: 0, notes: 0 });
  // The FORM-field variant on purpose: the pricing grid focuses its own first
  // row as its data arrives, and the generic hook would then stand down. This
  // one ignores grids, so the form opens on its top field; it still never takes
  // focus from a field the user is already in.
  useEffect(() => {
    if (loading || !pageRef.current) return;
    const ae = document.activeElement as HTMLElement | null;
    if (ae && (/^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) || ae.isContentEditable)) return;
    focusFirstFormControl(pageRef.current);
  }, [loading]);

  const [lookups, setLookups]   = useState<QuoteLookups | null>(null);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [contacts, setContacts] = useState<ContactOption[]>([]);

  const [pickCodes, setPickCodes] = useState(false);
  const [pickItems, setPickItems] = useState(false);
  const [rowSel, setRowSel]   = useState<Set<string | number>>(new Set());
  const [itemSel, setItemSel] = useState<Set<string | number>>(new Set());

  /** Final Amounts the user TYPED on item rows, by row id. Kept apart from the
   *  rows because the server works the discount back from them (legacy
   *  :8633-8648) and must be told which ones were typed. */
  const [finalOverrides, setFinalOverrides] = useState<Record<string, number>>({});

  // Grid edits are not native inputs, so the app-wide tracker cannot see them
  // (standing rule 15).
  useUnsavedGuard(dirty);

  // ── Load ─────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (id === null) return;
    setLoading(true);
    try {
      const bundle = await fetchQuote(id);
      setQuote(bundle.header);
      setRows(bundle.rows);
      setItems(bundle.items);
      setFinalOverrides({});
      setDirty(false);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load the quote.');
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => { void load(); }, [load]);

  // The prospect list keeps the quote's own prospect, so it is refetched once
  // the quote is known.
  useEffect(() => {
    void (async () => {
      try {
        setLookups(await fetchQuoteLookups(quote.prospectClientId));
      } catch (e) {
        notify.error(e instanceof Error ? e.message : 'Failed to load the dropdown lists.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote.oid]);

  // Projects follow the client; a prospect quote has none (CRMQuotes.cs :240).
  useEffect(() => {
    if (quote.isProspect || !quote.clientId) { setProjects([]); return; }
    let cancelled = false;
    void (async () => {
      try {
        const list = await fetchProjects(quote.clientId!);
        if (!cancelled) setProjects(list);
      } catch (e) {
        if (!cancelled) notify.error(e instanceof Error ? e.message : 'Failed to load the projects.');
      }
    })();
    return () => { cancelled = true; };
  }, [quote.isProspect, quote.clientId, notify]);

  // Contacts follow the client — or, for a prospect, its one primary contact.
  useEffect(() => {
    const clientId   = quote.isProspect ? null : quote.clientId;
    const prospectId = quote.isProspect ? quote.prospectClientId : null;
    if (!clientId && !prospectId) { setContacts([]); return; }
    let cancelled = false;
    void (async () => {
      try {
        const list = await fetchContacts(clientId, prospectId);
        if (!cancelled) setContacts(list);
      } catch (e) {
        if (!cancelled) notify.error(e instanceof Error ? e.message : 'Failed to load the contacts.');
      }
    })();
    return () => { cancelled = true; };
  }, [quote.isProspect, quote.clientId, quote.prospectClientId, notify]);

  // ── Header edits ─────────────────────────────────────────────────────────
  function patch(p: Partial<QuoteDetail>) {
    setQuote((cur) => ({ ...cur, ...p }));
    setDirty(true);
  }

  /** What changing the client or the prospect clears (legacy :3705-3717): the
   *  project, the contact and everything that was filled in from them. */
  const PARTY_RESET: Partial<QuoteDetail> = {
    projectId: null, projectCode: null, projectName: null,
    primaryContactId: null, primaryContactName: null, emailID: null,
    cellPhone: null, officePhone: null,
    billStreet1: null, billStreet2: null, billCity: null,
    billState: null, billZipCode: null, billCountry: null,
  };

  /** The Bill To address a client or prospect brings with it. Legacy shows it
   *  through the BO getters whenever the stored field is blank
   *  (CRMQuotes.cs :683-860); here it is filled in on the pick and stays
   *  editable. */
  function billFrom(party: PartyOption | undefined): Partial<QuoteDetail> {
    if (!party) return {};
    return {
      billStreet1: party.street1, billStreet2: party.street2, billCity: party.city,
      billState: party.state, billZipCode: party.zipCode, billCountry: party.country,
    };
  }

  function setProspectMode(isProspect: boolean) {
    // Legacy :3815-3829 clears both parties along with everything that hung
    // off them.
    patch({
      ...PARTY_RESET, isProspect,
      clientId: null, clientName: null, prospectClientId: null, prospectClientName: null,
    });
  }

  function setClient(clientId: string | null) {
    const party = lookups?.clients.find((c) => c.id === clientId);
    patch({ ...PARTY_RESET, clientId, clientName: party?.name ?? null, ...billFrom(party) });
  }

  function setProspect(prospectId: string | null) {
    const party = lookups?.prospects.find((c) => c.id === prospectId);
    patch({ ...PARTY_RESET, prospectClientId: prospectId, prospectClientName: party?.name ?? null, ...billFrom(party) });
  }

  function setProject(projectId: string | null) {
    const project = projects.find((p) => p.id === projectId);
    patch({ projectId, projectCode: project?.name ?? null, projectName: project?.projectName ?? null });
  }

  function setContact(contactId: string | null) {
    const contact = contacts.find((c) => c.id === contactId);
    // Legacy fills Email from the contact on every pick (CRMQuotes.cs :541-548)
    // and the phones whenever they are still blank (:575-640).
    patch({
      primaryContactId: contactId,
      primaryContactName: contact?.name ?? null,
      emailID: contact?.email ?? null,
      cellPhone: quote.cellPhone || contact?.mobilePhone || null,
      officePhone: quote.officePhone || contact?.officePhone || null,
    });
  }

  /** Legacy :3756-3775 — ticking copies Bill To into Report To; un-ticking
   *  clears Report To. While ticked, Report To simply mirrors Bill To. */
  function setSameAddress(same: boolean) {
    patch(same
      ? { sameAddress: true }
      : { sameAddress: false, shipStreet1: null, shipStreet2: null, shipCity: null,
          shipState: null, shipZipCode: null, shipCountry: null });
  }

  /** Legacy opens a "Quote Cancel Reason" popup the moment Cancel is ticked
   *  (:3778-3797), and a save with Cancel ticked and no reason is refused. */
  async function setCancel(cancel: boolean) {
    if (!cancel) { patch({ cancel: false, cancelReason: null }); return; }
    const reason = await prompt({
      title: 'Quote Cancel Reason',
      message: 'Why is this quote being cancelled?',
      required: true,
      initialValue: quote.cancelReason ?? '',
    });
    if (reason === null) return;           // the tick does not take
    patch({ cancel: true, cancelReason: reason.trim() });
  }

  // ── Pricing rows ─────────────────────────────────────────────────────────
  const rowInputs = useCallback((list: QuoteAnalysisRow[]): AnalysisRowInput[] =>
    list.map((r) => ({
      oid: r.oid,
      priceCodeId: r.isNew ? r.priceCodeId : null,
      sampleMatrixId: r.sampleMatrixId,
      qty: r.qty, discount: r.discount,
      testDescription: r.testDescription, remark: r.remark, sort: r.sort,
    })), []);

  const itemInputs = useCallback((list: QuoteItemRow[], overrides: Record<string, number>): ItemRowInput[] =>
    list.map((i) => ({
      oid: i.oid,
      itemPriceId: i.isNew ? i.itemPriceId : null,
      qty: i.qty, discount: i.discount,
      finalAmountOverride: overrides[i.oid] ?? null,
      remark: i.remark,
    })), []);

  /** Requests are numbered so a slow answer can never overwrite a newer one. */
  const calcSeq = useRef(0);

  /**
   * Send the draft to the server and take back the priced rows and the totals.
   * Returns false when the server refused it (e.g. a quantity of zero), in
   * which case nothing on screen changes.
   */
  const recalculate = useCallback(async (
    nextRows: AnalysisRowInput[], nextItems: ItemRowInput[],
    global?: { percent?: number; amount?: number },
  ): Promise<boolean> => {
    const seq = ++calcSeq.current;
    try {
      const priced = await calculateQuote({
        quoteId: id, rows: nextRows, items: nextItems,
        ...(global?.percent !== undefined ? { globalDiscount: global.percent } : {}),
        ...(global?.amount  !== undefined ? { globalDiscountAmount: global.amount } : {}),
      });
      if (seq !== calcSeq.current) return true;      // superseded
      setRows(priced.rows);
      setItems(priced.items);
      setQuote((cur) => ({
        ...cur,
        detailedAmount: priced.totals.detailedAmount,
        discountAmount: priced.totals.discountAmount,
        discount:       priced.totals.discount,
        totalAmount:    priced.totals.totalAmount,
        quotedAmount:   priced.totals.quotedAmount,
      }));
      setDirty(true);
      return true;
    } catch (e) {
      const message = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      notify.warning(message ?? (e instanceof Error ? e.message : 'Could not price the quote.'));
      return false;
    }
  }, [id, notify]);

  function addPriceCodes(picked: PriceCodeCandidate[]) {
    setPickCodes(false);
    if (picked.length === 0) return;
    const added: AnalysisRowInput[] = picked.map((c) => ({
      // The id chosen here becomes the row's id on the server, so the row has
      // one identity from this moment through to the save.
      oid: crypto.randomUUID(),
      priceCodeId: c.priceCodeId, sampleMatrixId: c.sampleMatrixId,
      qty: 1, discount: 0, sort: 0,
    }));
    void recalculate([...rowInputs(rows), ...added], itemInputs(items, finalOverrides));
  }

  function addItemCharges(picked: ItemChargeCandidate[]) {
    setPickItems(false);
    if (picked.length === 0) return;
    const added: ItemRowInput[] = picked.map((c) => ({
      oid: crypto.randomUUID(), itemPriceId: c.itemPriceId, qty: 1, discount: 0,
    }));
    void recalculate(rowInputs(rows), [...itemInputs(items, finalOverrides), ...added]);
  }

  function removeRows() {
    if (rowSel.size === 0) return;
    const kept = rows.filter((r) => !rowSel.has(r.oid));
    setRowSel(new Set());
    void recalculate(rowInputs(kept), itemInputs(items, finalOverrides));
  }

  function removeItems() {
    if (itemSel.size === 0) return;
    const kept = items.filter((i) => !itemSel.has(i.oid));
    const overrides = Object.fromEntries(
      Object.entries(finalOverrides).filter(([oid]) => kept.some((i) => i.oid === oid)));
    setItemSel(new Set());
    setFinalOverrides(overrides);
    void recalculate(rowInputs(rows), itemInputs(kept, overrides));
  }

  /** A whole number from a grid cell, or null when it is not one. */
  function wholeNumber(text: string): number | null {
    const n = Number(text);
    return text.trim() !== '' && Number.isInteger(n) ? n : null;
  }
  function decimalNumber(text: string): number | null {
    const n = Number(text);
    return text.trim() !== '' && Number.isFinite(n) ? n : null;
  }

  function commitRowCell(row: QuoteAnalysisRow, column: AppColumn<QuoteAnalysisRow>, text: string) {
    let next: QuoteAnalysisRow;
    switch (column.field) {
      case 'qty': {
        const qty = wholeNumber(text);
        // Legacy wording, verbatim (QuoteViewController :7464).
        if (qty === null || qty <= 0) { notify.warning('Qty value must be greater than 0.'); return; }
        next = { ...row, qty };
        break;
      }
      case 'discount': {
        const discount = decimalNumber(text);
        if (discount === null) { notify.warning('Discount must be a number.'); return; }
        // Mirrors the server rule (QuoteRules.DiscountTooHigh): refused here so the
        // grid never shows a negative price that the save would then reject.
        if (discount > 100) { notify.warning('Discount cannot be more than 100%.'); return; }
        next = { ...row, discount };
        break;
      }
      case 'sort': {
        const sort = wholeNumber(text);
        if (sort === null) { notify.warning('Sort must be a whole number.'); return; }
        next = { ...row, sort };
        break;
      }
      case 'testDescription': next = { ...row, testDescription: text.trim() || null }; break;
      case 'remark':          next = { ...row, remark: text.trim() || null };          break;
      default: return;
    }
    const list = rows.map((r) => (r.oid === row.oid ? next : r));
    // Text-only edits change no number, so they need no round trip.
    if (column.field === 'testDescription' || column.field === 'remark' || column.field === 'sort') {
      setRows(list);
      setDirty(true);
      return;
    }
    void recalculate(rowInputs(list), itemInputs(items, finalOverrides));
  }

  function commitItemCell(item: QuoteItemRow, column: AppColumn<QuoteItemRow>, text: string) {
    let next = item;
    let overrides = finalOverrides;
    switch (column.field) {
      case 'qty': {
        const qty = wholeNumber(text);
        if (qty === null || qty <= 0) { notify.warning('Qty value must be greater than 0.'); return; }
        next = { ...item, qty };
        // A new quantity makes a previously typed final amount meaningless.
        overrides = Object.fromEntries(Object.entries(finalOverrides).filter(([oid]) => oid !== item.oid));
        break;
      }
      case 'discount': {
        const discount = decimalNumber(text);
        if (discount === null) { notify.warning('Discount must be a number.'); return; }
        // Mirrors the server rule (QuoteRules.DiscountTooHigh): refused here so the
        // grid never shows a negative price that the save would then reject.
        if (discount > 100) { notify.warning('Discount cannot be more than 100%.'); return; }
        next = { ...item, discount };
        overrides = Object.fromEntries(Object.entries(finalOverrides).filter(([oid]) => oid !== item.oid));
        break;
      }
      case 'finalAmount': {
        // Legacy lets Final Amount be typed and works the discount back from it
        // (:8633-8648).
        const final = decimalNumber(text.replace(/[$,]/g, ''));
        if (final === null) { notify.warning('Final Amount must be a number.'); return; }
        overrides = { ...finalOverrides, [item.oid]: final };
        break;
      }
      case 'remark': {
        setItems(items.map((i) => (i.oid === item.oid ? { ...item, remark: text.trim() || null } : i)));
        setDirty(true);
        return;
      }
      default: return;
    }
    const list = items.map((i) => (i.oid === item.oid ? next : i));
    setFinalOverrides(overrides);
    void recalculate(rowInputs(rows), itemInputs(list, overrides));
  }

  /** The header's Discount % / Discount Amount are GLOBAL — typing either sets
   *  that discount on every row of both grids (legacy :3887-3975). */
  function applyGlobal(kind: 'percent' | 'amount', text: string) {
    const value = decimalNumber(text);
    if (value === null) return;
    if (rows.length === 0 && items.length === 0) {
      // Legacy wording, verbatim (LDMMessages "AddAnalysisPriceOrItemCharge").
      notify.info('Add the analysis price/ item charge  and continue.');
      return;
    }
    if (kind === 'percent' && value === quote.discount) return;
    if (kind === 'amount'  && value === quote.discountAmount) return;
    // A global discount replaces any typed final amounts.
    setFinalOverrides({});
    void recalculate(rowInputs(rows), itemInputs(items, {}),
                     kind === 'percent' ? { percent: value } : { amount: value });
  }

  // ── Save / delete ────────────────────────────────────────────────────────
  function toInput(): QuoteInput {
    return {
      lockVersion: isNew ? null : quote.lockVersion,
      title: quote.title, isProspect: quote.isProspect,
      clientId: quote.clientId, prospectClientId: quote.prospectClientId,
      tatId: quote.tatId, paymentMethodId: quote.paymentMethodId,
      expirationDate: quote.expirationDate,
      cancel: quote.cancel, cancelReason: quote.cancelReason,
      projectId: quote.projectId, quotedDate: quote.quotedDate, quotedById: quote.quotedById,
      remark: quote.remark,
      primaryContactId: quote.primaryContactId, emailID: quote.emailID,
      cellPhone: quote.cellPhone, officePhone: quote.officePhone,
      billStreet1: quote.billStreet1, billStreet2: quote.billStreet2, billCity: quote.billCity,
      billState: quote.billState, billZipCode: quote.billZipCode, billCountry: quote.billCountry,
      sameAddress: quote.sameAddress,
      shipStreet1: quote.shipStreet1, shipStreet2: quote.shipStreet2, shipCity: quote.shipCity,
      shipState: quote.shipState, shipZipCode: quote.shipZipCode, shipCountry: quote.shipCountry,
      ownerId: quote.ownerId, creditId: quote.creditId, sourceOpportunityId: quote.sourceOpportunityId,
      rows: rowInputs(rows),
      items: itemInputs(items, finalOverrides),
    };
  }

  /**
   * The Save trio. The rules are the server's (§14b.5); the three checked here
   * are only the ones a user hits most, shown before the confirm dialog rather
   * than after it, in legacy's order and legacy's words.
   */
  async function save(after: 'stay' | 'new' | 'close') {
    if (!quote.title?.trim()) { notify.error('Title should not empty.'); return; }
    if (quote.isProspect ? !quote.prospectClientId : !quote.clientId) {
      notify.error('Client should not empty.'); return;
    }
    if (rows.length === 0) { notify.error('Please add price codes in Analysis pricing.'); return; }

    let savedId: string | null = null;
    await runSave({
      formName: FORM_NAME,
      auditAction: isNew ? 'create' : 'edit',
      save: async () => {
        const res = isNew ? await createQuote(toInput()) : await updateQuote(id!, toInput());
        savedId = res.oid;
      },
      onSaved: () => {
        setDirty(false);
        onChanged();
        if (after === 'close') onClose();
        else if (after === 'new') onNew();
        else if (isNew && savedId) onCreated(savedId);
        else void load();
      },
    });
  }

  /**
   * Preview — the quote as a PDF. Legacy commits the form first
   * (`ObjectSpace.CommitChanges()`, :394) so the report shows what is on
   * screen; here a quote with unsaved edits is simply asked to save first,
   * rather than saved behind the user's back.
   */
  async function preview() {
    if (isNew || dirty) {
      notify.warning('Save the quote before previewing it.');
      return;
    }
    try {
      const filename = await downloadQuoteReport(id!);
      notify.success(`${filename} downloaded.`);
    } catch (e) {
      notify.error((await reportErrorMessage(e)) ?? 'Failed to generate the quote report.');
    }
  }

  const navPos  = id ? siblingIds.indexOf(id) : -1;
  const navPrev = navPos > 0 ? siblingIds[navPos - 1]! : null;
  const navNext = navPos >= 0 && navPos < siblingIds.length - 1 ? siblingIds[navPos + 1]! : null;

  // Keyboard: the Save trio + Cancel, declared next to the buttons they mirror
  // (standing rule 14, tier b).
  useHotkeys([
    { combo: 'mod+s',       label: 'Save',         group: 'Open Quote', run: () => void save('stay'),  disabled: loading, order: 1 },
    { combo: 'mod+shift+s', label: 'Save & New',   group: 'Open Quote', run: () => void save('new'),   disabled: loading, order: 2 },
    { combo: 'mod+enter',   label: 'Save & Close', group: 'Open Quote', run: () => void save('close'), disabled: loading, order: 3 },
    // A key press does not pass through the click that the unsaved-changes
    // tracker watches, so these three ask for themselves (standing rule 15).
    { combo: 'esc',         label: 'Cancel',       group: 'Open Quote', run: () => void guardedExit(() => onClose()), order: 4 },
    { combo: 'alt+p',       label: 'Preview',      group: 'Open Quote', order: 4.5,
      disabled: isNew || loading, run: () => void preview() },
    { combo: 'alt+left',    label: 'Previous record', group: 'Open Quote', order: 5,
      disabled: !onNavigate || !navPrev, run: () => void guardedExit(() => { if (navPrev) onNavigate?.(navPrev); }) },
    { combo: 'alt+right',   label: 'Next record',     group: 'Open Quote', order: 6,
      disabled: !onNavigate || !navNext, run: () => void guardedExit(() => { if (navNext) onNavigate?.(navNext); }) },
  ]);

  // ── Grid columns ─────────────────────────────────────────────────────────
  // Legacy CRMQuotes_AnalysisPricing_ListView (Model.xafml :11435). Editable,
  // exactly as there: TestDescription · Qty · Discount(%) · Remark · Sort.
  const rowColumns: AppColumn<QuoteAnalysisRow>[] = useMemo(() => [
    { field: 'testDescription', header: 'TestDescription', width: 170, edit: { type: 'text' } },
    { field: 'matrix',       header: 'Matrix',       width: 100 },
    { field: 'test',         header: 'Test',         width: 170 },
    { field: 'isGroup',      header: 'IsGroup',      width: 80, align: 'center',
      render: (r) => <input type="checkbox" checked={r.isGroup} readOnly disabled aria-label="IsGroup" /> },
    { field: 'method',       header: 'Method',       width: 120 },
    { field: 'sampleMatrix', header: 'SampleMatrix', width: 130 },
    { field: 'component',    header: 'Component',    width: 110 },
    { field: 'parameter',    header: 'Parameter',    width: 100 },
    { field: 'chargeTypeCode', header: 'ChargeType', width: 100, render: (r) => CHARGE_TYPE[r.chargeTypeCode] ?? '' },
    { field: 'tat',          header: 'TAT',          width: 90 },
    { field: 'priority',     header: 'Priority',     width: 90 },
    { field: 'priceCode',    header: 'PriceCode',    width: 110 },
    { field: 'qty',          header: 'Qty',          width: 70, align: 'right', type: 'number', edit: { type: 'number' } },
    { field: 'unitPrice',    header: 'UnitPrice',    width: 100, align: 'right', type: 'number', render: (r) => money.format(r.unitPrice) },
    { field: 'totalTierPrice', header: 'TotalTierPrice', width: 120, align: 'right', type: 'number', render: (r) => money.format(r.totalTierPrice) },
    { field: 'discount',     header: 'Discount(%)',  width: 110, align: 'right', type: 'number', edit: { type: 'number' }, render: (r) => r.discount.toFixed(2) },
    { field: 'finalAmount',  header: 'FinalAmount',  width: 120, align: 'right', type: 'number', render: (r) => money.format(r.finalAmount) },
    { field: 'remark',       header: 'Remark',       width: 160, edit: { type: 'text' } },
    { field: 'sort',         header: 'Sort',         width: 70, align: 'right', type: 'number', edit: { type: 'number' } },
  ], []);

  // Legacy CRMQuotes_QuotesItemChargePrice_ListView. Editable: Qty ·
  // Discount(%) · FinalAmount.
  const itemColumns: AppColumn<QuoteItemRow>[] = useMemo(() => [
    { field: 'itemCode',  header: 'ItemCode',  width: 120 },
    { field: 'itemName',  header: 'ItemName',  width: 220 },
    { field: 'category',  header: 'Category',  width: 140 },
    { field: 'unit',      header: 'Unit',      width: 90 },
    { field: 'unitPrice', header: 'UnitPrice', width: 110, align: 'right', type: 'number', render: (r) => money.format(r.unitPrice) },
    { field: 'qty',       header: 'Qty',       width: 70, align: 'right', type: 'number', edit: { type: 'number' } },
    { field: 'amount',    header: 'Amount',    width: 110, align: 'right', type: 'number', render: (r) => money.format(r.amount) },
    { field: 'discount',  header: 'Discount(%)', width: 110, align: 'right', type: 'number', edit: { type: 'number' }, render: (r) => r.discount.toFixed(2) },
    { field: 'finalAmount', header: 'FinalAmount', width: 120, align: 'right', type: 'number', edit: { type: 'number' }, render: (r) => money.format(r.finalAmount) },
    { field: 'remark',    header: 'Remark',    width: 160, edit: { type: 'text' } },
  ], []);

  // ── Field helpers ────────────────────────────────────────────────────────
  // The SHARED detail-view markup (`.sd-row` > `.sd-label` + `.sd-input`), so
  // label width, the 26px control, the 6px row gap and the yellow / grey cue
  // all come from SettingsDetailPage.css — the same rules the reference form
  // (Settings ▸ Inventory Management ▸ Items) renders with (standing rule 12).
  function field(label: string, control: ReactNode, required = false) {
    return (
      <div className="sd-row" key={label}>
        <label className="sd-label">
          {label}{required && <span className="req">*</span>}
        </label>
        {/* The label is a sibling, not a wrapper, so the control is named
            directly: that is what lets a screen reader say WHICH dropdown this is. */}
        {isValidElement<{ 'aria-label'?: string }>(control) && typeof control.type === 'string'
          ? cloneElement(control, { 'aria-label': label })
          : control}
      </div>
    );
  }

  /** A tick box, laid out as the shared page lays one out: an empty label cell
   *  keeps the box in the field column, the caption sits beside it. */
  function tick(label: string, checked: boolean, onChange: (v: boolean) => void) {
    return (
      <div className="sd-row sd-row--check" key={label}>
        <span className="sd-label" aria-hidden="true">&nbsp;</span>
        <label className="sd-check">
          <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
          {label}
        </label>
      </div>
    );
  }

  /**
   * One titled group of fields in the shared 50/50 column-major grid: the
   * `left` list runs down the first column, the `right` list down the second.
   * The grid fills column by column, so a shorter left list is padded with
   * blank rows — otherwise the right column's first fields would flow into the
   * space under it.
   */
  function section(title: string, left: ReactNode[], right: ReactNode[]) {
    const l = left.filter(Boolean);
    const r = right.filter(Boolean);
    const rowCount = Math.max(l.length, r.length);
    const pad = Array.from({ length: rowCount - l.length },
      (_, i) => <div className="sd-row" aria-hidden="true" key={`pad-${i}`} />);
    return (
      <>
        <h3 className="sd-section-h">{title}</h3>
        <div className="sd-grid sd-grid--colmajor sd-grid--side"
             style={{ '--sd-rows': String(rowCount) } as CSSProperties}>
          {l}{pad}{r}
        </div>
      </>
    );
  }

  function text(key: keyof QuoteDetail, opts: { required?: boolean; readOnly?: boolean } = {}) {
    const value = (quote[key] as string | null) ?? '';
    if (opts.readOnly) return <input type="text" className="sd-input readonly" readOnly value={value} />;
    const tint = opts.required && !value.trim() ? ' required-empty' : '';
    return (
      // autoComplete off: the Bill To / Report To fields look like a postal
      // address to the browser, which then offers to "Save address" to the
      // user's own account — a client's address is not the user's to keep.
      <input type="text" className={`sd-input${tint}`} value={value} maxLength={100} autoComplete="off"
             onChange={(e) => patch({ [key]: e.target.value || null } as Partial<QuoteDetail>)} />
    );
  }

  function select(
    value: string | null, options: Array<{ id: string; name: string | null }>,
    onPick: (id: string | null) => void, opts: { required?: boolean; disabled?: boolean } = {},
  ) {
    const tint = opts.required && !value ? ' required-empty' : '';
    return (
      <select className={`sd-input${tint}${opts.disabled ? ' readonly' : ''}`} disabled={opts.disabled}
              value={value ?? ''} onChange={(e) => onPick(e.target.value || null)}>
        <option value="" />
        {options.map((o) => <option key={o.id} value={o.id}>{o.name ?? ''}</option>)}
      </select>
    );
  }

  const readOnlyMoney = (value: number) =>
    <input type="text" className="sd-input readonly qt-money" readOnly value={money.format(value)} />;

  // Customer names repeat in this data, so a client is offered with its
  // address — the same reason legacy uses a grid lookup here rather than a
  // plain combo.
  const clientOptions = useMemo(() => (lookups?.clients ?? []).map((c) => ({
    id: c.id,
    name: [c.name, [c.street1, c.city].filter(Boolean).join(', ')].filter(Boolean).join(' — '),
  })), [lookups]);

  // ── Action bar, identical top and bottom ─────────────────────────────────
  // The shared `.sd-actionbar` / `.sd-abtn`, in the reference form's order and
  // wording: Save · Save and Close · Save and New · | · Cancel ("and", never
  // "&"; a thin divider before Cancel). Preview is this form's own action, so
  // it follows the save set. There is NO Delete here — the standard keeps
  // Delete out of an edit view; a quote is deleted from the list.
  const tip = (label: string, combo: string) => `${label} (${formatCombo(combo)})`;
  const actionBar = (bottom: boolean) => (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      <button type="button" className="sd-abtn primary" title={tip('Save', 'mod+s')}
              onClick={() => void save('stay')} disabled={loading}>
        <span className="tb-i">💾</span> Save
      </button>
      <button type="button" className="sd-abtn" title={tip('Save and Close', 'mod+enter')}
              onClick={() => void save('close')} disabled={loading}>
        <span className="tb-i">💾</span> Save and Close
      </button>
      <button type="button" className="sd-abtn" title={tip('Save and New', 'mod+shift+s')}
              onClick={() => void save('new')} disabled={loading}>
        <span className="tb-i">📄</span> Save and New
      </button>
      {!isNew && (
        <button type="button" className="sd-abtn" title={tip('Preview', 'alt+p')}
                onClick={() => void preview()} disabled={loading}>
          <span className="tb-i">👁</span> Preview
        </button>
      )}
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button type="button" className="sd-abtn cancel" title={tip('Cancel', 'esc')} onClick={onClose}>
        <span className="tb-i">↩︎</span> Cancel
      </button>
    </div>
  );

  const existingRowKeys = useMemo(() => new Set(rows.map((r) => candidateTestKey(r))), [rows]);
  const existingItemIds = useMemo(
    () => new Set(items.map((i) => (i.itemPriceId ?? '').toLowerCase())), [items]);
  const usedMatrixIds = useMemo(
    () => [...new Set(rows.map((r) => r.sampleMatrixId).filter((v): v is string => !!v))], [rows]);

  const ship = (billKey: keyof QuoteDetail, shipKey: keyof QuoteDetail) =>
    // While "Same Address" is ticked, Report To simply mirrors Bill To.
    quote.sameAddress
      ? <input type="text" className="sd-input readonly" readOnly value={(quote[billKey] as string | null) ?? ''} />
      : text(shipKey);

  // The four tabs under the form, in legacy's order.
  const tabs: Array<{ id: Tab; label: string; count: number }> = [
    { id: 'analysis',   label: 'Analysis Pricing', count: rows.length },
    { id: 'items',      label: 'Item Charge',      count: items.length },
    { id: 'attachment', label: 'Attachment',       count: docCounts.attachments },
    { id: 'note',       label: 'Notes',            count: docCounts.notes },
  ];
  const recordName = quote.quoteID ?? (isNew ? 'New' : '');

  return (
    <div className="bl-page mlv-host qt-detail">
      <div className="bl-grid-area">
        <div className="sd-wrap" ref={pageRef}>
          {/* Header — title + breadcrumb + prev/next record arrows */}
          <div className="sd-head">
            <div>
              <h2>
                {/* {BO} – {record}; legacy DetailView Caption "Open Quote" and
                    NullText "Autogenerate" on QuoteID until the first save. */}
                Open Quote – {quote.quoteID ?? 'Autogenerate'}
              </h2>
              <div className="sd-bc">
                <span>Accounting</span>
                <i className="ti ti-chevron-right" />
                <span>Quotes</span>
                <i className="ti ti-chevron-right" />
                <a onClick={onClose}>Open Quotes</a>
                <i className="ti ti-chevron-right" />
                <span>{recordName}</span>
              </div>
            </div>
            <div className="qt-headright">
              {/* This quote's own history — the shared, config-gated chip
                  (standing rule 11). A new quote has none yet. */}
              {!isNew && <AuditTrailButton variant="chip" formName={FORM_NAME} sourceId={id!} />}
              {onNavigate && siblingIds.length > 1 && (
                <div className="sd-navarrows">
                  <button type="button" className="sd-na" aria-label="Previous record"
                          title={tip('Previous record', 'alt+left')}
                          disabled={!navPrev} onClick={() => navPrev && onNavigate(navPrev)}>‹</button>
                  <button type="button" className="sd-na next" aria-label="Next record"
                          title={tip('Next record', 'alt+right')}
                          disabled={!navNext} onClick={() => navNext && onNavigate(navNext)}>›</button>
                </div>
              )}
            </div>
          </div>

          {actionBar(false)}

          <div className="sd-body">
            {section('Summary', [
              field('Quote ID', <input type="text" className="sd-input readonly" readOnly
                                       value={quote.quoteID ?? ''} placeholder="Autogenerate" />),
              field('Title', text('title', { required: true }), true),
              quote.isProspect
                ? field('Client', select(quote.prospectClientId, lookups?.prospects ?? [], setProspect, { required: true }), true)
                : field('Client', select(quote.clientId, clientOptions, setClient, { required: true }), true),
              tick('Is Prospect', quote.isProspect, setProspectMode),
              field('TAT', select(quote.tatId, lookups?.tats ?? [], (v) => patch({ tatId: v }))),
              field('Payment Method', select(quote.paymentMethodId, lookups?.paymentMethods ?? [], (v) => patch({ paymentMethodId: v }))),
              field('Expiration Date',
                <input type="date" className={`sd-input${quote.expirationDate ? '' : ' required-empty'}`}
                       min={today()} value={dateInput(quote.expirationDate)}
                       onChange={(e) => patch({ expirationDate: e.target.value ? `${e.target.value}T00:00:00` : null })} />,
                true),
              // Legacy hides Cancel until the quote has an id (CRMQuotes.cs :346)…
              !isNew && tick('Cancel', quote.cancel, (v) => void setCancel(v)),
              // …and Date Canceled / Cancel Reason until it is cancelled (:377).
              quote.cancel && field('Date Canceled',
                <input type="text" className="sd-input readonly" readOnly
                       value={fmtLocalDate(quote.dateCanceled)} placeholder="Set when saved" />),
              quote.cancel && field('Cancel Reason',
                <input type="text" className="sd-input readonly" readOnly value={quote.cancelReason ?? ''} />),
            ], [
              field('Project ID', select(quote.projectId, projects, setProject, { disabled: quote.isProspect })),
              field('Project Name', text('projectName', { readOnly: true })),
              field('Quoted Date',
                <input type="date" className="sd-input" value={dateInput(quote.quotedDate)}
                       onChange={(e) => patch({ quotedDate: e.target.value ? `${e.target.value}T00:00:00` : null })} />),
              field('Quoted By', select(quote.quotedById, lookups?.employees ?? [], (v) => patch({ quotedById: v }))),
              field('Amount', readOnlyMoney(quote.quotedAmount)),
              field('Remark',
                <textarea rows={1} className="sd-input" maxLength={100} value={quote.remark ?? ''}
                          onChange={(e) => patch({ remark: e.target.value || null })} />),
            ])}

            {section('Totals', [
              field('Detailed Amount', readOnlyMoney(quote.detailedAmount)),
              field('Discount Amount',
                <GlobalNumber key={`amt-${quote.discountAmount}`} value={quote.discountAmount}
                              onCommit={(t) => applyGlobal('amount', t)} />),
            ], [
              field('Discount %',
                <GlobalNumber key={`pct-${quote.discount}`} value={quote.discount}
                              onCommit={(t) => applyGlobal('percent', t)} />),
              field('Amount', readOnlyMoney(quote.totalAmount)),
            ])}

            {section('Contacts', [
              field('Primary Contact', select(quote.primaryContactId, contacts, setContact)),
              field('Email ID', text('emailID', { required: !!quote.primaryContactId }), !!quote.primaryContactId),
            ], [
              field('Cell Phone', text('cellPhone')),
              field('Office Phone', text('officePhone')),
            ])}

            {section('Bill To Address', [
              field('Street 1', text('billStreet1')),
              field('Street 2', text('billStreet2')),
              field('City', text('billCity')),
            ], [
              field('State', text('billState')),
              field('Zip Code', text('billZipCode')),
              field('Country', text('billCountry')),
            ])}

            {section('Report to Address', [
              tick('Same Address', quote.sameAddress, setSameAddress),
              field('Street 1', ship('billStreet1', 'shipStreet1')),
              field('Street 2', ship('billStreet2', 'shipStreet2')),
              field('City', ship('billCity', 'shipCity')),
            ], [
              <div className="sd-row" aria-hidden="true" key="pad-same" />,
              field('State', ship('billState', 'shipState')),
              field('Zip Code', ship('billZipCode', 'shipZipCode')),
              field('Country', ship('billCountry', 'shipCountry')),
            ])}

            {section('Account Management', [
              field('Owner', select(quote.ownerId, lookups?.employees ?? [], (v) => patch({ ownerId: v }))),
              field('Credit', select(quote.creditId, lookups?.credits ?? [], (v) => patch({ creditId: v }))),
            ], [
              field('Source Opportunity', select(quote.sourceOpportunityId, lookups?.opportunities ?? [], (v) => patch({ sourceOpportunityId: v }))),
              field('Status', <input type="text" className="sd-input readonly" readOnly
                                     value={quote.status ?? (isNew ? 'Pending Validation' : '')} />),
            ])}

            {/* The child tabs — the shared tab strip with its panel joined
                beneath it, as on the reference form. All four panels stay
                mounted and the inactive ones are hidden, so switching tabs
                costs no refetch and keeps each grid's selection. */}
            <div className="sd-assoc-tabbar sd-childtabbar" role="tablist">
              {tabs.map((t) => (
                <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
                        className={`sd-assoc-tab${tab === t.id ? ' active' : ''}`}
                        onClick={() => setTab(t.id)}>
                  {t.label}<span className="sd-assoc-tabn">{t.count}</span>
                </button>
              ))}
            </div>

            <div role="tabpanel" className="sd-childtabpanel qt-tabpanel" hidden={tab !== 'analysis'}>
              <div className="qt-tabbar">
                <button type="button" className="ca-btn" onClick={() => setPickCodes(true)}>
                  <i className="ti ti-plus tb-i-green" aria-hidden="true" /> Add
                </button>
                <button type="button" className="ca-btn" onClick={removeRows} disabled={rowSel.size === 0}>
                  <i className="ti ti-minus tb-i-red" aria-hidden="true" /> Remove
                </button>
              </div>
              <AppDataGrid<QuoteAnalysisRow>
                rows={rows}
                columns={rowColumns}
                getRowId={(r) => r.oid}
                loading={loading}
                selectable
                selectedIds={rowSel}
                onSelectionChange={setRowSel}
                onCellCommit={commitRowCell}
                maximizable
                exportFilename={`quote-${quote.quoteID ?? 'new'}-analysis-pricing`}
                emptyText="No price codes yet. Use Add to choose them."
              />
              <div className="qt-sums">
                <span>Total Qty <b>{rows.reduce((n, r) => n + r.qty, 0)}</b></span>
                <span>Total Final Amount <b>{money.format(rows.reduce((n, r) => n + r.finalAmount, 0))}</b></span>
              </div>
            </div>

            <div role="tabpanel" className="sd-childtabpanel qt-tabpanel" hidden={tab !== 'items'}>
              <div className="qt-tabbar">
                <button type="button" className="ca-btn" onClick={() => setPickItems(true)}>
                  <i className="ti ti-plus tb-i-green" aria-hidden="true" /> Add
                </button>
                <button type="button" className="ca-btn" onClick={removeItems} disabled={itemSel.size === 0}>
                  <i className="ti ti-minus tb-i-red" aria-hidden="true" /> Remove
                </button>
              </div>
              <AppDataGrid<QuoteItemRow>
                rows={items}
                columns={itemColumns}
                getRowId={(r) => r.oid}
                loading={loading}
                selectable
                selectedIds={itemSel}
                onSelectionChange={setItemSel}
                onCellCommit={commitItemCell}
                maximizable
                exportFilename={`quote-${quote.quoteID ?? 'new'}-item-charge`}
                emptyText="No item charges."
              />
              <div className="qt-sums">
                <span>Total Qty <b>{items.reduce((n, i) => n + i.qty, 0)}</b></span>
                <span>Total Amount <b>{money.format(items.reduce((n, i) => n + i.amount, 0))}</b></span>
                <span>Total Final Amount <b>{money.format(items.reduce((n, i) => n + i.finalAmount, 0))}</b></span>
              </div>
            </div>

            {/* Attachment | Notes — legacy CRMQuotes_Attachments_ListView and
                CRMQuotes_Note_ListView. The shared documents panel, pointed at
                this quote and driven by the tab strip above. Its rows save on
                their own, at once, and are not part of the quote's Save — so
                they do not mark the form as changed. */}
            <div role="tabpanel" className="sd-childtabpanel qt-tabpanel" data-unsaved-ignore
                 hidden={tab !== 'attachment' && tab !== 'note'}>
              <PreLoginDocsPanel cocOid={id} owner={QUOTE_DOCS} embedded
                                 activeTab={tab === 'note' ? 'note' : 'attachment'}
                                 onCounts={setDocCounts} />
            </div>
          </div>

          {actionBar(true)}
        </div>
      </div>

      <PriceCodePickerDialog
        open={pickCodes}
        sampleMatrices={lookups?.sampleMatrices ?? []}
        existingKeys={existingRowKeys}
        initialMatrixIds={usedMatrixIds}
        onAdd={addPriceCodes}
        onClose={() => setPickCodes(false)}
      />
      <ItemChargePickerDialog
        open={pickItems}
        existingIds={existingItemIds}
        onAdd={addItemCharges}
        onClose={() => setPickItems(false)}
      />
    </div>
  );
}

/**
 * One of the two GLOBAL discount boxes. It holds what is being typed and only
 * hands it over on blur or Enter — committing on every keystroke would re-price
 * the whole quote at "1" on the way to "15". Re-keyed by the parent whenever the
 * server returns a new value, so it always shows the stored number at rest.
 */
function GlobalNumber({ value, onCommit }: { value: number; onCommit: (text: string) => void }) {
  const [draft, setDraft] = useState(value.toFixed(2));
  return (
    <input type="number" step="any" className="sd-input qt-money" value={draft}
           onChange={(e) => setDraft(e.target.value)}
           onBlur={() => onCommit(draft)}
           onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onCommit(draft); } }} />
  );
}

/** A wire date → the `yyyy-MM-dd` an <input type="date"> needs. Goes through the
 *  shared helper, so no date formatting is inlined here (C2). */
function dateInput(v: string | null): string {
  if (!v) return '';
  const [m, d, y] = fmtLocalDate(v).split('/');
  return y && m && d ? `${y}-${m}-${d}` : '';
}

function today(): string {
  const [m, d, y] = fmtLocalDate(new Date().toISOString()).split('/');
  return `${y}-${m}-${d}`;
}
